import { Colors } from '@factorialco/deprecated-design-system'
import { ThemeColors } from '@factorialco/deprecated-design-system'

export type StatusTagColor = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

export type TCard<TItem = unknown> = {
  id: string
  position?: string | null
  projections: React.ReactNode[]
  title: string
  item?: TItem | undefined
}

export interface TColumn<TVariables> {
  id: string
  statusColor: StatusTagColor | Colors | ThemeColors
  title: string
  variables: TVariables
}

export interface DragItem<TVariables, TItem = unknown> {
  id: string
  title: string
  item?: TItem | undefined
  sourceColumnVariables: TVariables
}

const cardKey = Symbol('card')
export type TCardData<TItem = unknown> = {
  [cardKey]: true
  card: TCard<TItem>
  columnId: string
  rect: DOMRect
}

export const getCardData = <TItem = unknown>({
  card,
  columnId,
  rect,
}: Omit<TCardData<TItem>, typeof cardKey> & { columnId: string }): TCardData<TItem> => {
  return {
    [cardKey]: true,
    card,
    columnId,
    rect,
  }
}

export const isCardData = <TItem = unknown>(
  value: Record<string | symbol, unknown>
): value is TCardData<TItem> => {
  return Boolean(value[cardKey])
}

const cardDropTargetKey = Symbol('card-drop-target')
type TCardDropTargetData<TItem = unknown> = {
  [cardDropTargetKey]: true
  card: TCard<TItem>
  columnId: string
}

export const isCardDropTargetData = <TItem = unknown>(
  value: Record<string | symbol, unknown>
): value is TCardDropTargetData<TItem> => {
  return Boolean(value[cardDropTargetKey])
}

export const getCardDropTargetData = <TItem = unknown>({
  card,
  columnId,
}: Omit<TCardDropTargetData<TItem>, typeof cardDropTargetKey> & {
  columnId: string
}): TCardDropTargetData<TItem> => {
  return {
    [cardDropTargetKey]: true,
    card,
    columnId,
  }
}

const columnKey = Symbol('column')
type TColumnData<TVariables> = {
  [columnKey]: true
  column: TColumn<TVariables>
}

export const getColumnData = <TVariables>({
  column,
}: Omit<TColumnData<TVariables>, typeof columnKey>): TColumnData<TVariables> => {
  return {
    [columnKey]: true,
    column,
  }
}

export const isColumnData = <TVariables>(
  value: Record<string | symbol, unknown>
): value is TColumnData<TVariables> => {
  return Boolean(value[columnKey])
}

export const isColumnVariables = <TVariables>(value: unknown): value is TVariables => {
  return typeof value === 'object' && value !== null
}

export const isShallowEqual = (
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
): boolean => {
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) {
    return false
  }
  return keys1.every((key1) => Object.is(obj1[key1], obj2[key1]))
}
